<template>
  <div
    v-if="hasFields"
    class="attribution-card"
    :class="{ 'attribution-card--open': isOpen }"
  >
    <button
      type="button"
      class="attribution-card__header"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <span class="attribution-card__header-left">
        <span class="attribution-card__icon" aria-hidden="true">
          <BaseIcon name="bullseye" size="16" />
        </span>
        <span class="attribution-card__header-text">
          <span class="attribution-card__title">
            {{ title || $t('attribution.title') }}
          </span>
          <span v-if="!isOpen && summaryText" class="attribution-card__summary">
            {{ summaryText }}
          </span>
        </span>
      </span>
      <span class="attribution-card__header-right">
        <span v-if="!isOpen && channelChips.length" class="attribution-card__chips">
          <span
            v-for="chip in channelChips"
            :key="chip"
            class="attribution-card__chip"
          >
            {{ chip }}
          </span>
        </span>
        <BaseIcon name="chevron" size="14" class="attribution-card__chevron" />
      </span>
    </button>

    <div v-show="isOpen" class="attribution-card__body">
      <dl class="attribution-card__list">
        <div
          v-for="field in visibleFields"
          :key="field.key"
          class="attribution-card__row"
        >
          <dt class="attribution-card__label">{{ field.label }}</dt>
          <dd class="attribution-card__value">
            <a
              v-if="field.isUrl"
              :href="field.value"
              target="_blank"
              rel="noopener noreferrer"
              class="attribution-card__link"
              @click.stop
            >
              {{ field.displayValue }}
              <BaseIcon name="external-link" size="12" />
            </a>
            <span v-else>{{ field.displayValue }}</span>
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>

<script>
  import { computed, ref, watch, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const FIELD_DEFS = [
    { key: 'utm_source', labelKey: 'attribution.utm_source' },
    { key: 'utm_medium', labelKey: 'attribution.utm_medium' },
    { key: 'utm_campaign', labelKey: 'attribution.utm_campaign' },
    { key: 'utm_content', labelKey: 'attribution.utm_content' },
    { key: 'utm_term', labelKey: 'attribution.utm_term' },
    { key: 'landing_page', labelKey: 'attribution.landing_page', isUrl: true },
    { key: 'referrer', labelKey: 'attribution.referrer', isUrl: true },
    { key: 'gclid', labelKey: 'attribution.gclid' },
    { key: 'fbclid', labelKey: 'attribution.fbclid' },
    { key: 'ttclid', labelKey: 'attribution.ttclid' },
    { key: 'first_touch_at', labelKey: 'attribution.first_touch_at', isDate: true },
  ];

  const truncateUrl = (url) => {
    if (!url || url.length <= 52) return url;
    try {
      const parsed = new URL(url);
      const path = parsed.pathname + parsed.search;
      const shortPath = path.length > 32 ? `${path.slice(0, 29)}…` : path;
      return `${parsed.hostname}${shortPath}`;
    } catch {
      return `${url.slice(0, 49)}…`;
    }
  };

  export default {
    name: 'AttributionCard',
    components: { BaseIcon },
    props: {
      attribution: { type: Object, default: null },
      title: { type: String, default: '' },
      defaultOpen: { type: Boolean, default: false },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const isOpen = ref(props.defaultOpen);

      watch(
        () => props.defaultOpen,
        (value) => {
          isOpen.value = value;
        }
      );

      const toggle = () => {
        isOpen.value = !isOpen.value;
      };

      const visibleFields = computed(() => {
        if (!props.attribution) return [];

        return FIELD_DEFS.reduce((fields, def) => {
          const raw = props.attribution[def.key];
          if (raw == null || raw === '') return fields;

          let displayValue = String(raw);
          if (def.isDate) {
            displayValue = dayjs(raw).format('DD-MM-YYYY HH:mm');
          } else if (def.isUrl) {
            displayValue = truncateUrl(String(raw));
          }

          fields.push({
            key: def.key,
            label: $t(def.labelKey),
            value: String(raw),
            displayValue,
            isUrl: !!def.isUrl,
          });
          return fields;
        }, []);
      });

      const hasFields = computed(() => visibleFields.value.length > 0);

      const channelChips = computed(() => {
        if (!props.attribution) return [];
        return ['utm_source', 'utm_medium']
          .map((key) => props.attribution[key])
          .filter((value) => value != null && value !== '')
          .map((value) => String(value));
      });

      const summaryText = computed(() => {
        if (channelChips.value.length) return '';
        const landing = props.attribution?.landing_page;
        if (landing) return truncateUrl(String(landing));
        const referrer = props.attribution?.referrer;
        if (referrer) return truncateUrl(String(referrer));
        if (props.attribution?.gclid) return 'Google Ads';
        if (props.attribution?.fbclid) return 'Meta Ads';
        if (props.attribution?.ttclid) return 'TikTok Ads';
        return '';
      });

      return {
        isOpen,
        toggle,
        visibleFields,
        hasFields,
        channelChips,
        summaryText,
      };
    },
  };
</script>

<style scoped lang="scss">
  .attribution-card {
    @include card('sm');
    background: white;
    overflow: hidden;
    padding: 0;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      width: 100%;
      padding: 0.75rem 1rem;
      border: 0;
      background: transparent;
      cursor: pointer;
      text-align: left;
      color: inherit;

      @include hover {
        background: var(--slate-50);
      }
    }

    &__header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      min-width: 0;
      flex: 1;
    }

    &__header-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      background: var(--brand-50);
      color: var(--brand-600);
      flex-shrink: 0;
    }

    &__header-text {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__title {
      @include fontSize('sm');
      font-weight: 600;
      color: var(--slate-800);
      line-height: 1.3;
    }

    &__summary {
      @include fontSize('sm');
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__chips {
      display: none;
      align-items: center;
      gap: 0.375rem;

      @media (min-width: 480px) {
        display: flex;
      }
    }

    &__chip {
      @include fontSize('sm');
      max-width: 7rem;
      padding: 0.125rem 0.5rem;
      border-radius: 999px;
      background: var(--slate-100);
      color: var(--slate-600);
      @include ellipsis(1);
    }

    &__chevron {
      color: var(--slate-400);
      transition: transform 0.15s ease;
      transform: rotate(180deg);
    }

    &--open &__chevron {
      transform: rotate(0deg);
    }

    &--open &__header {
      border-bottom: 1px solid var(--slate-100);
    }

    &__body {
      padding: 0.75rem 1rem 1rem;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      margin: 0;
    }

    &__row {
      display: grid;
      grid-template-columns: minmax(6.5rem, 8.5rem) 1fr;
      gap: 0.375rem 0.75rem;
      align-items: start;
    }

    &__label {
      @include fontSize('sm');
      color: var(--slate-500);
      font-weight: 500;
      margin: 0;
    }

    &__value {
      @include fontSize('sm');
      color: var(--slate-800);
      margin: 0;
      word-break: break-word;
    }

    &__link {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      color: var(--brand-600);
      text-decoration: none;

      @include hover {
        text-decoration: underline;
      }
    }
  }
</style>
